import { Alert } from '@ksyos/design-system';
import { useEffect, useRef, useState } from 'react';
import type { ConclusieDiagnoseOption } from '@common/enums';
import { getServiceLabel } from '@common/customControls/getServiceLabel';
import {
    DermaConsultationAssetDropzoneExtraActions,
    FilesUrlBase,
    PractitionerRoleInRequest,
    ProcessEntity,
    ToastType,
} from '@common/enums';
import type { IAsset } from '../../../../interfaces/asset';
import type { IMedication } from '../../../../interfaces/medication';
import type { ICustomControlRequestAddEditProps } from '../../interfaces';
import {
    getOrderedAssets,
    getOrderedPhotoAssetIds,
    getPhotoAssetIdKey,
} from '../../../../../../common/utils/photoAssetUtils';
import { attachmentLimit } from '../../../../../../common/validation/asset';
import { getJson } from '../../../../../store/fetch';
import { translateKey } from '../../../../../translate';
import { checkPreviousExaminationHasAssets } from '../../../../../utils/checkPreviousExaminationHasAssets';
import { showServiceToast } from '../../../../../utils/showToast';
import { CancelButton } from '../../../../elements/carePathButtons/CancelButton';
import { SaveButton } from '../../../../elements/carePathButtons/SaveButton';
import { SaveSendButton } from '../../../../elements/carePathButtons/SaveSendButton';
import { ButtonContainer, Form, FormDivider, HeaderDivider, InputCheckbox } from '../../../../elements/common';
import { FormContextProvider } from '../../../../elements/formContext';
import { FormLabel } from '../../../../elements/forms/FormLabel';
import { HorizontalFormGroup } from '../../../../elements/forms/HorizontalFormGroup';
import { InputContainer } from '../../../../elements/forms/InputContainer';
import { InputTextCheckbox } from '../../../../elements/InputTextCheckbox';
import { ProceedWithoutFiles } from '../../../../elements/ProceedWithoutFiles';
import { TextAreaField } from '../../../../fields/TextArea';
import { TriStateField } from '../../../../fields/TriState';
import { ProfessionalSummaryData } from '../../../professionalSummaryData/ProfessionalSummaryData';
import { addFormAnalytics, addFormConversion, addTrackEvent, addTrackFormSubmit } from '../../utils/formAnalytics';
import { AssetDropzoneWithActions } from '../../utils/v1/AssetDropzoneWithActions';
import { CareProviderSelector } from '../../utils/v1/CareProviderSelector';
import { DiagnosisConclusionSelection } from '../../utils/v1/DiagnosisConclusionSelection';
import { MedicationsAddEdit } from '../../utils/v1/medications/MedicationsAddEdit';
import { NewOrEditRequestProcessContainer } from '../../utils/v1/NewOrEditRequestProcessContainer';
import { ServiceConsumerSupervisorSelector } from '../../utils/v1/ServiceConsumerSupervisorSelector';
import { ServiceOrganizationProvider } from '../../utils/v1/ServiceOrganizationProvider';
import { DermaSnelCV1FollowingSteps } from './DermaSnelCV1FollowingSteps';
import { ModalUploadPictureFromDermaApp } from './ModalUploadPictureFromDermaApp';

export interface IDermaSnelCV1RequestCustomControlData {
    behandeling_geprobeerd: boolean | null;
    behandeling_geprobeerd_remark: string | null;
    beschrijving_klachten: string | null;
    bewezen_allergie: boolean | null;
    bewezen_allergie_remark: string | null;
    chronisch: boolean | null;
    conclusie_diagnose: ConclusieDiagnoseOption;
    duur_klachten: string | null;
    familiaire_huidziekten: boolean | null;
    familiaire_huidziekten_remark: string | null;
    lichaamsdeel: string | null;
    recidief: boolean | null;
    symptomen: {
        anders: string | null;
        bloeden: boolean;
        cosmetisch: boolean;
        jeuk: boolean;
        pijn: boolean;
    };
    toelichting: string | null;
    vraag: string | null;
}

export function DermaSnelCV1RequestAddEdit(props: ICustomControlRequestAddEditProps) {
    const [buttonDisabled, setButtonDisabled] = useState<boolean>();
    const [proceedWithoutFiles, setProceedWithoutFiles] = useState(false);
    const [showUploadFromAppModal, setShowUploadFromAppModal] = useState(false);
    const [orderedAssets, setOrderedAssets] = useState<IAsset[]>([]);

    const intervalRef = useRef<NodeJS.Timeout | undefined>(undefined);
    const formRef = useRef<HTMLFormElement>(null);
    const saveSendButtonRef = useRef<HTMLButtonElement>(null);

    const assetsLimitExceeded =
        (props.request.customControlData.files?.length || 0 + props.request.assets.length) > attachmentLimit;

    useEffect(() => {
        if (assetsLimitExceeded === false && props.request.id && showUploadFromAppModal === false) {
            reloadAssets();
            intervalRef.current = setInterval(() => reloadAssets(), 4000);
        }

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [assetsLimitExceeded, props.request.id, showUploadFromAppModal]);

    useEffect(() => {
        setOrderedAssets(getOrderedAssets(props.request));
    }, [props.request]);

    useEffect(() => {
        addFormAnalytics(formRef.current, saveSendButtonRef.current);
    }, []);

    return (
        <NewOrEditRequestProcessContainer
            isFirstItem={props.isFirstItem}
            itemState={props.itemState}
            process={props.process}
            service={props.service}
            title={getServiceLabel(props.service.code, 'request', props.service.name)}
        >
            <Form
                formRef={formRef}
                id="DermaSnelCV1RequestAddEditId"
                name="DermaSnelCV1RequestAddEdit"
                onSubmit={async () => null}
            >
                {addEditForm()}

                <ButtonContainer
                    alignRight
                    buttons={[
                        <CancelButton
                            cancel={props.onCancel}
                            entity={ProcessEntity.request}
                            key="cancel"
                            serviceCode={props.service.code}
                            serviceType={props.service.serviceType}
                        />,
                        <SaveButton
                            buttonDisabled={buttonDisabled || assetsLimitExceeded}
                            key="save"
                            save={async () => {
                                await saveRequest('draft');
                            }}
                        />,
                        <SaveSendButton
                            buttonDisabled={buttonDisabled || assetsLimitExceeded}
                            key="saveSend"
                            saveSend={async () => {
                                await saveRequest('final');
                            }}
                            saveSendButtonRef={saveSendButtonRef}
                        />,
                    ]}
                />

                {proceedWithoutFiles && (
                    <ProceedWithoutFiles
                        entity={ProcessEntity.request}
                        onNo={async () => {
                            setProceedWithoutFiles(false);
                            setButtonDisabled(false);
                        }}
                        onYes={async () => {
                            setProceedWithoutFiles(false);
                            await saveRequest('final');
                        }}
                        serviceType={props.service.serviceType}
                    />
                )}
            </Form>
        </NewOrEditRequestProcessContainer>
    );

    function addEditForm() {
        const customControlData: IDermaSnelCV1RequestCustomControlData = props.request.customControlData;

        return (
            <FormContextProvider
                value={{
                    errors: props.errors,
                    item: customControlData,
                    onChange: async (key, value) => await props.handleChangeCustomControl({ [key]: value }),
                    onDoValidate: async (key) => await props.validateOneCustomControl(key),
                    placeholderGroupName: 'request',
                }}
            >
                <CareProviderSelector
                    {...props}
                    entity={props.request}
                />

                <ServiceOrganizationProvider
                    errors={props.errors}
                    initialOrganizationId={props.request.selectedDiagnosticReportProviderOrganizationId}
                    initialPractitionerId={props.request.selectedDiagnosticReportProviderPractitionerId}
                    onChange={async (organizationId, practitionerId) => {
                        await props.handleChange('selectedDiagnosticReportProviderOrganizationId', organizationId);
                        await props.handleChange('selectedDiagnosticReportProviderPractitionerId', practitionerId);
                    }}
                    onDoValidate={async () => await props.validateOne('selectedDiagnosticReportProviderOrganizationId')}
                    roleOfPractitionersInList={PractitionerRoleInRequest.specialist}
                    searchInNetwork={true}
                    serviceProviderServiceId={props.service.id}
                    serviceType={props.service.serviceType}
                />

                <ServiceConsumerSupervisorSelector {...props} />

                <FormDivider />

                <HeaderDivider text={translateKey('procedure.procedure')} />

                {props.request.id && (
                    <ModalUploadPictureFromDermaApp
                        isOpen={showUploadFromAppModal}
                        onCancel={async () => {
                            setShowUploadFromAppModal(false);
                        }}
                        onProceed={async () => {
                            setShowUploadFromAppModal(false);
                            await props.reloadAssets();
                        }}
                        requestId={props.request.id}
                    />
                )}

                {assetsLimitExceeded && (
                    <Alert appearance="warning">
                        Je hebt te veel bestanden toegevoegd. Blijf binnen de limiet van 12 bestanden.
                    </Alert>
                )}

                {!showUploadFromAppModal && (
                    <AssetDropzoneWithActions
                        assets={orderedAssets}
                        assetsBaseUrl={FilesUrlBase.requests}
                        cleanDropzoneAfterCompletingFiles={Boolean(props.request.id)}
                        extraActions={
                            props.previousRequest
                                ? []
                                : [
                                      {
                                          actionId: DermaConsultationAssetDropzoneExtraActions.uploadFromApp,
                                          icon: 'telephone',
                                          label: "Foto's via de app toevoegen",
                                      },
                                  ]
                        }
                        onAction={(actionId: string) => {
                            if (actionId === DermaConsultationAssetDropzoneExtraActions.uploadFromApp) {
                                addTrackEvent('DermaSnelCV1', 'clicked', 'ModalUploadDermaApp');
                                setShowUploadFromAppModal(true);
                                props.onSaveRequest('draft');
                            }
                        }}
                        onChange={handleAssetsChange}
                        onUploading={async (isUploading) => setButtonDisabled(isUploading)}
                        processActivityId={props.request.id}
                        service={props.service}
                        showAdditionalFilesCheckbox={props.previousRequest !== undefined}
                        showUseExistingExamination={true}
                        updateNewRequestPreviousRequest={props.updateNewRequestPreviousRequest}
                    />
                )}
                {props.previousRequest === undefined && (
                    <HorizontalFormGroup>
                        <Alert appearance="info">Let op: Vergeet niet een afbeelding te uploaden!</Alert>
                    </HorizontalFormGroup>
                )}

                <FormDivider />

                <HeaderDivider text={translateKey('carePlan.anamnese')} />

                <HorizontalFormGroup hasErrors={props.errors.has('symptomen')}>
                    <FormLabel> Symptomen </FormLabel>
                    <InputContainer>
                        <InputCheckbox
                            checked={customControlData.symptomen.jeuk}
                            error={props.errors.get('customControlData.symptomen.jeuk')}
                            name="Jeuk"
                            onChange={async (value) =>
                                await props.handleChangeCustomControl<IDermaSnelCV1RequestCustomControlData>({
                                    symptomen: { ...customControlData.symptomen, jeuk: value },
                                })
                            }
                            onDoValidate={async () => await props.validateOneCustomControl('symptomen')}
                            text="Jeuk"
                        />
                        <InputCheckbox
                            checked={customControlData.symptomen.pijn}
                            error={props.errors.get('customControlData.symptomen.pijn')}
                            name="Pijn"
                            onChange={async (value) =>
                                await props.handleChangeCustomControl<IDermaSnelCV1RequestCustomControlData>({
                                    symptomen: { ...customControlData.symptomen, pijn: value },
                                })
                            }
                            onDoValidate={async () => await props.validateOneCustomControl('symptomen')}
                            text="Pijn"
                        />
                        <InputCheckbox
                            checked={customControlData.symptomen.bloeden}
                            error={props.errors.get('customControlData.symptomen.bloeden')}
                            name="Bloeden"
                            onChange={async (value) =>
                                await props.handleChangeCustomControl<IDermaSnelCV1RequestCustomControlData>({
                                    symptomen: { ...customControlData.symptomen, bloeden: value },
                                })
                            }
                            onDoValidate={async () => await props.validateOneCustomControl('symptomen')}
                            text="Bloeden"
                        />
                        <InputCheckbox
                            checked={customControlData.symptomen.cosmetisch}
                            error={props.errors.get('customControlData.symptomen.cosmetisch')}
                            name="Cosmetisch"
                            onChange={async (value) =>
                                await props.handleChangeCustomControl<IDermaSnelCV1RequestCustomControlData>({
                                    symptomen: { ...customControlData.symptomen, cosmetisch: value },
                                })
                            }
                            onDoValidate={async () => await props.validateOneCustomControl('symptomen')}
                            text="Cosmetisch"
                        />
                        <InputTextCheckbox
                            error={props.errors.get('customControlData.symptomen.andersSymptomen')}
                            name="Anders, namelijk"
                            onChange={async (value) =>
                                await props.handleChangeCustomControl<IDermaSnelCV1RequestCustomControlData>({
                                    symptomen: { ...customControlData.symptomen, anders: value },
                                })
                            }
                            onDoValidate={async () => await props.validateOneCustomControl('symptomen')}
                            placeholder="Anders, namelijk..."
                            text={customControlData.symptomen.anders}
                        />
                    </InputContainer>
                </HorizontalFormGroup>

                <TextAreaField
                    error={props.errors.get('customControlData.beschrijving_klachten')}
                    fieldKey="beschrijving_klachten"
                    hasErrors={props.errors.has('customControlData.beschrijving_klachten')}
                    label="Beschrijving klachten"
                    placeholder="Evt. beschrijven klachten en huidbeeld, aanvullende anamnese, etc..."
                />

                <TextAreaField
                    error={props.errors.get('customControlData.duur_klachten')}
                    fieldKey="duur_klachten"
                    hasErrors={props.errors.has('customControlData.duur_klachten')}
                    label="Duur klachten"
                    placeholder="Omschrijf de duur van de klachten..."
                />

                <TextAreaField
                    error={props.errors.get('customControlData.lichaamsdeel')}
                    fieldKey="lichaamsdeel"
                    hasErrors={props.errors.has('customControlData.lichaamsdeel')}
                    label="Lichaamsdeel"
                    placeholder="Om welk lichaamsdeel gaat het..."
                />

                <TriStateField
                    error={props.errors.get('customControlData.chronisch')}
                    fieldKey="chronisch"
                    hasErrors={props.errors.has('customControlData.chronisch')}
                    label="Chronisch"
                    value={customControlData.chronisch}
                />

                <TriStateField
                    error={props.errors.get('customControlData.recidief')}
                    fieldKey="recidief"
                    hasErrors={props.errors.has('customControlData.recidief')}
                    label="Recidief"
                    value={customControlData.recidief}
                />

                <TriStateField
                    error={props.errors.get('customControlData.behandeling_geprobeerd')}
                    fieldKey="behandeling_geprobeerd"
                    hasErrors={props.errors.has('customControlData.behandeling_geprobeerd')}
                    label="Behandeling geprobeerd"
                    value={customControlData.behandeling_geprobeerd}
                />

                {customControlData.behandeling_geprobeerd && (
                    <TextAreaField
                        error={props.errors.get('customControlData.behandeling_geprobeerd_remark')}
                        fieldKey="behandeling_geprobeerd_remark"
                        hasErrors={props.errors.has('customControlData.behandeling_geprobeerd_remark')}
                        label=" "
                        placeholder="Beschrijf de behandeling die je hebt geprobeerd en de duur van deze behandeling..."
                    />
                )}

                <TriStateField
                    error={props.errors.get('customControlData.familiaire_huidziekten')}
                    fieldKey="familiaire_huidziekten"
                    hasErrors={props.errors.has('customControlData.familiaire_huidziekten')}
                    label="Familiaire huidziekten"
                    value={customControlData.familiaire_huidziekten}
                />

                {customControlData.familiaire_huidziekten && (
                    <TextAreaField
                        error={props.errors.get('customControlData.familiaire_huidziekten_remark')}
                        fieldKey="familiaire_huidziekten_remark"
                        hasErrors={props.errors.has('customControlData.familiaire_huidziekten_remark')}
                        label=" "
                        placeholder="Beschrijf de familiaire huidziekten..."
                    />
                )}

                <TriStateField
                    error={props.errors.get('customControlData.bewezen_allergie')}
                    fieldKey="bewezen_allergie"
                    hasErrors={props.errors.has('customControlData.bewezen_allergie')}
                    label="Bewezen allergie"
                    value={customControlData.bewezen_allergie}
                />

                {customControlData.bewezen_allergie && (
                    <TextAreaField
                        error={props.errors.get('customControlData.bewezen_allergie_remark')}
                        fieldKey="bewezen_allergie_remark"
                        hasErrors={props.errors.has('customControlData.bewezen_allergie_remark')}
                        label=" "
                        placeholder="Beschrijf de bewezen allergie..."
                    />
                )}

                <FormDivider />

                <MedicationsAddEdit
                    entity={ProcessEntity.request}
                    entityId={props.request.id}
                    errors={props.errors}
                    handleChangePatientUsesMedication={async (patientUsesMedication: boolean) =>
                        await props.handleChange('patientUsesMedication', patientUsesMedication)
                    }
                    onChange={async (medications: IMedication[]) =>
                        await props.handleChange('requestMedications', medications)
                    }
                    patientUsesMedication={props.request.patientUsesMedication}
                    validateMedications={async () => await props.validateOne('requestMedications')}
                    validatePatientUsesMedication={async () => await props.validateOne('patientUsesMedication')}
                />

                <ProfessionalSummaryData
                    entity={ProcessEntity.request}
                    entityId={props.request.id}
                    errors={props.errors}
                    handleChangeAllergies={async (allergies) => await props.handleChange('allergies', allergies)}
                    handleChangeEpisodes={async (episodesAndJournal) =>
                        await props.handleChange('requestEpisodesAndJournal', episodesAndJournal)
                    }
                    handleChangeLabResults={async (labResults) =>
                        await props.handleChange('requestLabResults', labResults)
                    }
                    validateEpisodesAndJournals={async () => await props.validateOne('requestEpisodesAndJournal')}
                />

                <HeaderDivider text={translateKey('request.question')} />

                <DiagnosisConclusionSelection
                    conclusie_diagnose={customControlData.conclusie_diagnose}
                    diagnosis={props.request.diagnosis}
                    errors={props.errors}
                    handleChangeConclusieDiagnose={props.handleChangeCustomControl}
                    handleChangeDiagnosisConsideration={async (diagnosis) =>
                        await props.handleChange('diagnosis', diagnosis)
                    }
                    service={props.service}
                    validateConclusieDiagnose={props.validateOneCustomControl}
                    validateDiagnosisConsideration={async () => props.validateOne('diagnosis')}
                />
                <TextAreaField
                    error={props.errors.get('customControlData.vraag')}
                    fieldKey="vraag"
                    hasErrors={props.errors.has('customControlData.vraag')}
                    isRequired
                    label="Vraag"
                    placeholder="Stel een vraag aan de dermatoloog..."
                />
                <TextAreaField
                    error={props.errors.get('customControlData.toelichting')}
                    fieldKey="toelichting"
                    hasErrors={props.errors.has('customControlData.toelichting')}
                    label="Toelichting"
                    placeholder="Een toelichting, opmerkingen of vragen..."
                />

                <DermaSnelCV1FollowingSteps />
            </FormContextProvider>
        );
    }

    async function saveRequest(saveType: 'draft' | 'final') {
        const formNode = formRef.current;
        const buttonNode = saveSendButtonRef.current;
        setButtonDisabled(true);

        if (!proceedWithoutFiles) {
            addTrackFormSubmit(buttonNode);
        }

        const validatedRequest = await props.validateRequest(saveType);
        if (!validatedRequest) {
            setButtonDisabled(false);

            return;
        }

        if (
            !proceedWithoutFiles &&
            saveType === 'final' &&
            props.request.assets.length === 0 &&
            !checkPreviousExaminationHasAssets(props.previousRequest?.procedure)
        ) {
            setProceedWithoutFiles(true);

            return;
        }

        const requestId = await props.onSaveRequest(saveType);

        if (saveType === 'draft') {
            showServiceToast(
                ToastType.success,
                'toast.draftSuccess',
                props.service,
                'request',
                translateKey('toast.draftDefaultMessage')
            );
            await props.onSave(requestId);
        } else {
            const result = await getJson(
                `/api/organizations/${props.request.selectedDiagnosticReportProviderOrganizationId}`
            );
            showServiceToast(
                ToastType.success,
                'toast.submitSuccess',
                props.service,
                'request',
                translateKey('toast.consultRequestSubmit', result.json.name)
            );
            await props.onSave(requestId);
            addFormConversion(formNode);
        }
    }

    async function handleAssetsChange(assets: IAsset[]) {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
        }

        const appPhotos = getOrderedPhotoAssetIds(props.request);
        const removedAppPhotos = appPhotos.filter((photo) => assets.every((asset) => asset.id !== photo.photoAssetId));

        for (const { volgnummer } of removedAppPhotos) {
            const photoAssetIdKey = getPhotoAssetIdKey(volgnummer);
            await props.handleChangeCustomControl({
                [photoAssetIdKey]: null,
            });
        }

        await props.handleChange('assets', assets);
        await props.onSaveRequest('draft');
    }

    async function reloadAssets() {
        await props.reloadAssets();
    }
}

export function dermaSnelCV1RequestInitializeCustomControlData() {
    return {
        behandeling_geprobeerd: null,
        behandeling_geprobeerd_remark: null,
        beschrijving_klachten: null,
        bewezen_allergie: null,
        bewezen_allergie_remark: null,
        chronisch: null,
        conclusie_diagnose: null,
        duur_klachten: null,
        familiaire_huidziekten: null,
        familiaire_huidziekten_remark: null,
        lichaamsdeel: null,
        recidief: null,
        symptomen: {
            anders: null,
            bloeden: false,
            cosmetisch: false,
            jeuk: false,
            pijn: false,
        },
        toelichting: null,
        vraag: null,
    };
}
